/**
* จุดนัดพบตามสถานีรถไฟฟ้า BTS / MRT / ARL / SRT Red Line / Gold Line
*
* COMPLIANCE (do not weaken): ทุกจุดในไฟล์นี้เป็น "พื้นที่ให้บริการ /
* จุดนัดหมาย" เท่านั้น ไม่ใช่สำนักงานที่มีพนักงานประจำ จึงต้อง
* `serviceAreaOnly: true` เสมอ — ห้ามใส่ street / postalCode เด็ดขาด
* (การประกาศที่อยู่ที่ไม่มีจริงถือเป็น local spam ตามนโยบาย Google)
*/
import { branch, type Branch, type ServiceFocus } from "./types";

type Line = { key: string; label: string; focus?: ServiceFocus[] };

const FOCUS_CBD: ServiceFocus[] = [
  "notary",
  "translation",
  "sworn",
  "lgz",
  "visa",
  "apostille",
  "corporate",
];
const FOCUS_STD: ServiceFocus[] = ["notary", "translation", "sworn", "lgz", "moj", "pcc"];

/** [slug, ชื่อสถานี, districtSlug, cbd?] */
type S = [string, string, string, boolean?];

const LINES: { line: Line; stations: S[]; province?: string }[] = [
  {
    line: { key: "bts-sukhumvit", label: "BTS สายสุขุมวิท" },
    stations: [
      ["mo-chit", "หมอชิต", "chatuchak", true],
      ["saphan-khwai", "สะพานควาย", "phaya-thai"],
      ["ari", "อารีย์", "phaya-thai", true],
      ["sanam-pao", "สนามเป้า", "phaya-thai"],
      ["victory-monument", "อนุสาวรีย์ชัยสมรภูมิ", "ratchathewi", true],
      ["phaya-thai", "พญาไท", "ratchathewi", true],
      ["ratchathewi", "ราชเทวี", "ratchathewi", true],
      ["siam", "สยาม", "pathum-wan", true],
      ["chit-lom", "ชิดลม", "pathum-wan", true],
      ["phloen-chit", "เพลินจิต", "pathum-wan", true],
      ["nana", "นานา", "watthana", true],
      ["asok", "อโศก", "watthana", true],
      ["phrom-phong", "พร้อมพงษ์", "watthana", true],
      ["thong-lo", "ทองหล่อ", "watthana", true],
      ["ekkamai", "เอกมัย", "watthana", true],
      ["phra-khanong", "พระโขนง", "khlong-toei"],
      ["on-nut", "อ่อนนุช", "phra-khanong"],
      ["bang-chak", "บางจาก", "phra-khanong"],
      ["punnawithi", "ปุณณวิถี", "phra-khanong"],
      ["udom-suk", "อุดมสุข", "bang-na"],
      ["bang-na-bts", "บางนา", "bang-na"],
      ["bearing", "แบริ่ง", "bang-na"],
      ["ha-yaek-lat-phrao", "ห้าแยกลาดพร้าว", "chatuchak", true],
      ["phahon-yothin-24", "พหลโยธิน 24", "chatuchak"],
      ["ratchayothin", "รัชโยธิน", "chatuchak"],
      ["sena-nikhom", "เสนานิคม", "chatuchak"],
      ["kasetsart", "มหาวิทยาลัยเกษตรศาสตร์", "chatuchak"],
      ["royal-forest", "กรมป่าไม้", "lat-phrao"],
      ["bang-bua", "บางบัว", "bang-khen"],
      ["11th-infantry", "กรมทหารราบที่ 11", "bang-khen"],
      ["wat-phra-sri", "วัดพระศรีมหาธาตุ", "bang-khen"],
      ["anusawari-lak-si", "อนุสาวรีย์หลักสี่", "lak-si"],
      ["sai-yud", "สายหยุด", "lak-si"],
      ["saphan-mai", "สะพานใหม่", "sai-mai"],
      ["bhumibol-hospital", "โรงพยาบาลภูมิพลอดุลยเดช", "sai-mai"],
      ["rtaf-museum", "พิพิธภัณฑ์กองทัพอากาศ", "sai-mai"],
      ["yaek-kor-por-aor", "แยก คปอ.", "don-mueang"],
    ],
  },
  {
    line: { key: "bts-silom", label: "BTS สายสีลม" },
    stations: [
      ["national-stadium", "สนามกีฬาแห่งชาติ", "pathum-wan", true],
      ["ratchadamri", "ราชดำริ", "pathum-wan", true],
      ["sala-daeng", "ศาลาแดง", "bang-rak", true],
      ["chong-nonsi", "ช่องนนทรี", "sathon", true],
      ["saint-louis", "เซนต์หลุยส์", "sathon"],
      ["surasak", "สุรศักดิ์", "bang-rak", true],
      ["saphan-taksin", "สะพานตากสิน", "bang-rak"],
      ["krung-thon-buri", "กรุงธนบุรี", "khlong-san"],
      ["wongwian-yai", "วงเวียนใหญ่", "thon-buri"],
      ["pho-nimit", "โพธิ์นิมิตร", "thon-buri"],
      ["talat-phlu", "ตลาดพลู", "thon-buri"],
      ["wutthakat", "วุฒากาศ", "chom-thong"],
      ["bang-wa", "บางหว้า", "phasi-charoen"],
    ],
  },
  {
    line: { key: "bts-gold", label: "BTS สายสีทอง" },
    stations: [
      ["charoen-nakhon", "เจริญนคร", "khlong-san"],
      ["khlong-san", "คลองสาน", "khlong-san"],
    ],
  },
  {
    line: { key: "mrt-blue", label: "MRT สายสีน้ำเงิน" },
    stations: [
      ["hua-lamphong", "หัวลำโพง", "pathum-wan", true],
      ["sam-yan", "สามย่าน", "pathum-wan", true],
      ["si-lom", "สีลม", "bang-rak", true],
      ["lumphini", "ลุมพินี", "pathum-wan", true],
      ["khlong-toei", "คลองเตย", "khlong-toei"],
      ["qsncc", "ศูนย์การประชุมแห่งชาติสิริกิติ์", "khlong-toei", true],
      ["sukhumvit-mrt", "สุขุมวิท", "watthana", true],
      ["phetchaburi", "เพชรบุรี", "ratchathewi", true],
      ["phra-ram-9", "พระราม 9", "huai-khwang", true],
      ["thailand-cultural-centre", "ศูนย์วัฒนธรรมแห่งประเทศไทย", "huai-khwang", true],
      ["huai-khwang", "ห้วยขวาง", "huai-khwang"],
      ["sutthisan", "สุทธิสาร", "din-daeng"],
      ["ratchadaphisek", "รัชดาภิเษก", "chatuchak"],
      ["lat-phrao-mrt", "ลาดพร้าว", "chatuchak", true],
      ["phahon-yothin", "พหลโยธิน", "chatuchak"],
      ["chatuchak-park", "สวนจตุจักร", "chatuchak"],
      ["kamphaeng-phet", "กำแพงเพชร", "chatuchak"],
      ["bang-sue", "บางซื่อ", "bang-sue"],
      ["tao-poon", "เตาปูน", "bang-sue"],
      ["bang-pho", "บางโพ", "bang-sue"],
      ["bang-o", "บางอ้อ", "bang-phlat"],
      ["bang-phlat", "บางพลัด", "bang-phlat"],
      ["sirindhorn", "สิรินธร", "bang-phlat"],
      ["bang-yi-khan", "บางยี่ขัน", "bang-phlat"],
      ["bang-khun-non", "บางขุนนนท์", "bangkok-noi"],
      ["fai-chai", "ไฟฉาย", "bangkok-noi"],
      ["itsaraphap", "อิสรภาพ", "bangkok-yai"],
      ["tha-phra", "ท่าพระ", "bangkok-yai"],
      ["bang-phai", "บางไผ่", "phasi-charoen"],
      ["phetkasem-48", "เพชรเกษม 48", "phasi-charoen"],
      ["phasi-charoen", "ภาษีเจริญ", "phasi-charoen"],
      ["bang-khae", "บางแค", "bang-khae"],
      ["lak-song", "หลักสอง", "bang-khae"],
      ["sanam-chai", "สนามไชย", "phra-nakhon", true],
      ["sam-yot", "สามยอด", "phra-nakhon"],
      ["wat-mangkon", "วัดมังกร", "pom-prap-sattru-phai"],
    ],
  },
  {
    line: { key: "mrt-yellow", label: "MRT สายสีเหลือง" },
    stations: [
      ["phawana", "ภาวนา", "wang-thonglang"],
      ["chok-chai-4", "โชคชัย 4", "lat-phrao"],
      ["lat-phrao-71", "ลาดพร้าว 71", "wang-thonglang"],
      ["lat-phrao-83", "ลาดพร้าว 83", "wang-thonglang"],
      ["mahatthai", "มหาดไทย", "wang-thonglang", true],
      ["lat-phrao-101", "ลาดพร้าว 101", "bang-kapi"],
      ["bang-kapi", "บางกะปิ", "bang-kapi", true],
      ["yaek-lam-sali", "แยกลำสาลี", "bang-kapi"],
      ["si-kritha", "ศรีกรีฑา", "saphan-sung"],
      ["hua-mak", "หัวหมาก", "bang-kapi"],
      ["kalantan", "กลันตัน", "suan-luang"],
      ["si-nut", "ศรีนุช", "suan-luang"],
      ["srinagarindra-38", "ศรีนครินทร์ 38", "prawet"],
      ["suan-luang-rama-9", "สวนหลวง ร.9", "prawet"],
      ["si-udom", "ศรีอุดม", "prawet"],
      ["si-iam", "ศรีเอี่ยม", "bang-na"],
    ],
  },
  {
    line: { key: "mrt-pink", label: "MRT สายสีชมพู" },
    stations: [
      ["lak-si-pink", "หลักสี่", "lak-si"],
      ["ratchaphat-phranakhon", "ราชภัฏพระนคร", "bang-khen"],
      ["ram-inthra-3", "รามอินทรา 3", "bang-khen"],
      ["lat-pla-khao", "ลาดปลาเค้า", "lat-phrao"],
      ["ram-inthra-31", "รามอินทรา 31", "bang-khen"],
      ["mai-lap", "มัยลาภ", "bueng-kum"],
      ["watcharaphon", "วัชรพล", "bang-khen"],
      ["ram-inthra-40", "รามอินทรา 40", "khan-na-yao"],
      ["khu-bon", "คู้บอน", "khan-na-yao"],
      ["ram-inthra-83", "รามอินทรา 83", "khan-na-yao"],
      ["wongwaen-ram-inthra", "วงแหวนรามอินทรา", "khan-na-yao"],
      ["nopparat", "นพรัตน์", "khan-na-yao"],
      ["bang-chan", "บางชัน", "khlong-sam-wa"],
      ["setthabutbamphen", "เศรษฐบุตรบำเพ็ญ", "min-buri"],
      ["talat-min-buri", "ตลาดมีนบุรี", "min-buri"],
      ["min-buri", "มีนบุรี", "min-buri", true],
    ],
  },
  {
    line: { key: "arl", label: "Airport Rail Link" },
    stations: [
      ["ratchaprarop", "ราชปรารภ", "ratchathewi", true],
      ["makkasan", "มักกะสัน", "ratchathewi", true],
      ["ramkhamhaeng", "รามคำแหง", "bang-kapi"],
      ["hua-mak-arl", "หัวหมาก", "bang-kapi"],
      ["ban-thap-chang", "บ้านทับช้าง", "prawet"],
      ["lat-krabang", "ลาดกระบัง", "lat-krabang", true],
    ],
  },
  {
    line: { key: "srt-red", label: "รถไฟฟ้าชานเมืองสายสีแดง" },
    stations: [
      ["krung-thep-aphiwat", "สถานีกลางกรุงเทพอภิวัฒน์", "chatuchak", true],
      ["chatuchak-red", "จตุจักร", "chatuchak"],
      ["wat-samian-nari", "วัดเสมียนนารี", "chatuchak"],
      ["bang-khen-red", "บางเขน", "chatuchak"],
      ["thung-song-hong", "ทุ่งสองห้อง", "lak-si"],
      ["lak-si-red", "หลักสี่", "lak-si"],
      ["kan-kheha", "การเคหะ", "don-mueang"],
      ["don-mueang", "ดอนเมือง", "don-mueang", true],
      ["bang-bamru", "บางบำหรุ", "bang-phlat"],
      ["taling-chan", "ตลิ่งชัน", "taling-chan"],
    ],
  },
];

export const TRANSIT_BRANCHES: Branch[] = LINES.flatMap(({ line, stations, province }) =>
  stations.map(([slug, nameTh, districtSlug, cbd]) =>
    branch({
      id: `${line.key}-${slug}`,
      name: `จุดนัดพบ ${line.label} สถานี${nameTh}`,
      shortName: `${line.label.split(" ")[0]} ${nameTh}`,
      street: "",
      locality: nameTh,
      region: province ? province : "กรุงเทพมหานคร",
      postalCode: "",
      serviceAreaOnly: true,
      meetingPoint: `บริเวณ${line.label} สถานี${nameTh} (นัดหมายล่วงหน้า ระบุทางออกที่สะดวก)`,
      branchType: "district",
      provinceSlug: "bangkok",
      districtSlug,
      landmarks: [`สถานี${nameTh}`],
      transit: [`${line.label} สถานี${nameTh}`],
      serviceFocus: cbd ? FOCUS_CBD : FOCUS_STD,
      note: `นัดรับรองลายมือชื่อและรับ-ส่งเอกสารบริเวณสถานี${nameTh} สะดวกสำหรับลูกค้าที่เดินทางด้วย${line.label}`,
    }),
  ),
);
